import React, { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera } from 'three';

interface CameraZoomPulseProps {
  inMs?: number;   // zoom-in duration
  outMs?: number;  // zoom-out duration
  deltaFov?: number; // magnitude of fov change (positive means zoom-in amount)
  delay?: number;  // delay before starting pulse
  onDone?: () => void;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInCubic = (t: number) => t * t * t;

const CameraZoomPulse: React.FC<CameraZoomPulseProps> = ({ inMs = 220, outMs = 380, deltaFov = 4, delay = 0, onDone }) => {
  const { camera } = useThree();
  const baseFov = useRef<number>(60);
  const t0 = useRef<number>(performance.now());
  const started = useRef<boolean>(false);
  const finished = useRef<boolean>(false);
  const delayId = useRef<number | null>(null);

  useEffect(() => {
    const persp = camera as PerspectiveCamera;
    baseFov.current = persp.fov ?? 60;
    if (delay > 0) {
      delayId.current = window.setTimeout(() => { started.current = true; t0.current = performance.now(); }, delay);
    } else {
      started.current = true; t0.current = performance.now();
    }
    return () => {
      if (delayId.current) window.clearTimeout(delayId.current);
      const persp = camera as PerspectiveCamera;
      persp.fov = baseFov.current;
      persp.updateProjectionMatrix();
    };
  }, [camera, delay]);

  useFrame(() => {
    if (!started.current || finished.current) return;
    const now = performance.now();
    const elapsed = now - t0.current;
    const total = inMs + outMs;
    const persp = camera as PerspectiveCamera;
    if (elapsed <= inMs) {
      const u = easeOutCubic(Math.min(1, elapsed / inMs));
      persp.fov = baseFov.current - deltaFov * u;
    } else if (elapsed <= total) {
      const u = easeInCubic(Math.min(1, (elapsed - inMs) / outMs));
      persp.fov = baseFov.current - deltaFov * (1 - u);
    } else {
      persp.fov = baseFov.current;
      finished.current = true;
      if (onDone) onDone();
    }
    persp.updateProjectionMatrix();
  });

  return null;
};

export default CameraZoomPulse;
